import {
  Checkbox,
  ConfigProvider,
  Input,
  InputNumber,
  Radio,
  SelectProps,
  Typography
} from 'antd'
import DisabledContext from 'antd/es/config-provider/DisabledContext'
import { createStyles } from 'antd-style'
import dayjs from 'dayjs'
import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  ReactElement,
  ReactNode,
  useContext
} from 'react'

import { dayMonthYearFormatWithDot } from 'src/libs'

const EMPTY = '—'

/** Set around a select shown as text, so the fields inside it know they are read-only. */
const ReadOnlyViewContext = createContext(false)

/**
 * True inside a read-only (disabled) form, e.g. a saved fișa before "Editează". A form that is being edited,
 * such as the yearly update modal, is not affected.
 */
export const useIsReadOnlyForm = () => {
  const disabled = useContext(DisabledContext)
  const inReadOnlyView = useContext(ReadOnlyViewContext)

  return disabled || inReadOnlyView
}

/** The selected option of a choice field as plain text, "—" when nothing was answered. */
export const ReadOnlyChoice = ({ label }: { label?: ReactNode }) => (
  <Typography.Text>{label ?? EMPTY}</Typography.Text>
)

/** A select that only shows its label: the select resolves the label of the stored id, as when editing. */
export const readOnlySelectProps: SelectProps = {
  variant: 'borderless',
  open: false,
  suffixIcon: null,
  allowClear: false,
  tabIndex: -1
}

const useStyles = createStyles(() => ({
  select: {
    // Shown, not used: the select only resolves the label.
    '.ant-select': { pointerEvents: 'none' },
    '.ant-select-selector': {
      paddingInline: '0 !important',
      cursor: 'default !important'
    },
    '.ant-select-selection-search': { insetInlineStart: '0 !important' }
  }
}))

/** Shows a select as text: enabled (so it is not greyed out) but closed and borderless. */
export const ReadOnlySelectView = ({ children }: { children: ReactNode }) => {
  const { styles } = useStyles()

  return (
    <ReadOnlyViewContext.Provider value>
      <ConfigProvider componentDisabled={false}>
        <div className={styles.select}>{children}</div>
      </ConfigProvider>
    </ReadOnlyViewContext.Provider>
  )
}

const TEXT_INPUTS = new Set<unknown>([Input, Input.TextArea, InputNumber])

const isEmpty = (value: unknown) =>
  value == null || value === '' || (Array.isArray(value) && value.length === 0)

type ControlProps = {
  children: ReactElement
  value?: unknown
  checked?: boolean
}

/**
 * The value of a form field as text. Form.Item passes the value to its direct child, this control, which
 * formats it by the kind of the field it replaces: text and numbers as they are, dates as dd.MM.yyyy, a
 * checkbox as its label when checked, a radio group as its selected option, and a select (or a field that renders itself as text) closed and
 * borderless, so the label of a stored id is resolved as when editing.
 */
export const ReadOnlyControl = ({ children, value, checked }: ControlProps) => {
  if (!isValidElement<Record<string, unknown>>(children)) return null

  const props = children.props

  if (children.type === Checkbox) {
    return (
      <ReadOnlyChoice
        label={checked ? (props.children as ReactNode) : undefined}
      />
    )
  }

  if (isEmpty(value)) {
    // A disabled field explains why it is empty in its placeholder, e.g. a hidden name.
    const placeholder =
      props.disabled && typeof props.placeholder === 'string'
        ? props.placeholder
        : undefined

    return <ReadOnlyChoice label={placeholder} />
  }

  if (children.type === Radio.Group) {
    const selected = Children.toArray(props.children as ReactNode).find(
      (option) =>
        isValidElement<{ value?: unknown }>(option) &&
        option.props.value === value
    )

    return (
      <ReadOnlyChoice
        label={
          isValidElement<{ children?: ReactNode }>(selected)
            ? selected.props.children
            : String(value)
        }
      />
    )
  }

  if (dayjs.isDayjs(value)) {
    return <ReadOnlyChoice label={value.format(dayMonthYearFormatWithDot)} />
  }

  if (TEXT_INPUTS.has(children.type)) {
    return (
      <Typography.Text style={{ whiteSpace: 'pre-wrap' }}>
        {String(value)}
      </Typography.Text>
    )
  }

  return (
    <ReadOnlySelectView>
      {cloneElement(children, { ...readOnlySelectProps, value })}
    </ReadOnlySelectView>
  )
}
